import { useState, useRef, useCallback, useLayoutEffect } from 'react';

/**
 * Measure how wide an element actually renders, for use as an <img sizes="...">
 * value.
 *
 * Do not compute `sizes` from props or from Tailwind classes. MoviePoster's
 * `size` prop said 64px while every call site overrode it via className to
 * ~384px, so the browser fetched a w92 poster for a 384px box - a 6x upscale
 * that was visibly grainy. Measuring removes that whole class of mistake, and
 * it also tracks responsive grids without hard-coding breakpoint arithmetic.
 *
 * Returns [width, setRef]. `width` is null until the first measurement; callers
 * should omit srcSet entirely while it is null so the browser falls back to the
 * full-size `src` rather than guessing too small.
 *
 * @param {object} [forwardedRef] optional ref that also needs the node (e.g. the
 *   IntersectionObserver ref from useLazyTMDB)
 */
export const useMeasuredWidth = (forwardedRef = null) => {
  const [width, setWidth] = useState(null);
  const nodeRef = useRef(null);

  const setRef = useCallback((el) => {
    nodeRef.current = el;
    if (forwardedRef) forwardedRef.current = el;
  }, [forwardedRef]);

  useLayoutEffect(() => {
    const el = nodeRef.current;
    if (!el) return undefined;

    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth((prev) => (prev === w ? prev : w));
    };

    measure();

    // jsdom has no ResizeObserver; the single measurement above still applies.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [width, setRef];
};

export default useMeasuredWidth;
